import { useState, useEffect } from "react";
import { Users, Calendar, GraduationCap, User } from "lucide-react";
import OrganicShape from "./OrganicShape";
import { fetchBoardPerformance } from "../../services/businessDashApi";

const BoardPerformance = () => {
  const [apiPerformanceData, setApiPerformanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load performance metrics from API
  useEffect(() => {
    const loadPerformance = async () => {
      try {
        setIsLoading(true);
        const data = await fetchBoardPerformance();
        setApiPerformanceData(data);
      } catch (error) {
        console.error("Failed to load performance data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPerformance();
  }, []);

  const metrics = apiPerformanceData?.metrics || apiPerformanceData || {};

  const performanceData = {
    attendanceRate:
      metrics.average_attendance_rate ?? metrics.attendanceRate ?? 85,
    churnRate: metrics.churn_rate ?? metrics.churnRate ?? null,
    averageAge: metrics.average_age ?? metrics.averageAge ?? null,
    youngestAge: metrics.youngest_age ?? metrics.min_age ?? metrics.youngestAge ?? null,
    oldestAge: metrics.oldest_age ?? metrics.max_age ?? metrics.oldestAge ?? null,
    educationLevel: metrics.education_level ?? metrics.average_education_score ?? null,
    lowestEducation: metrics.lowest_education ?? metrics.min_education ?? null,
    highestEducation: metrics.highest_education ?? metrics.max_education ?? null,
    leisureActivities:
      metrics.leisure_activities ?? metrics.leisureActivities ?? null,
  };

  const rawEducations =
    apiPerformanceData?.board_educations ??
    apiPerformanceData?.boardEducations ??
    apiPerformanceData?.educations ??
    [];

  const boardEducations = rawEducations.map((e, idx) => ({
    id: e.id ?? idx + 1,
    title: e.title || e.name || "",
    date: e.date || e.held_at || e.created_at || "",
    participants: Array.isArray(e.participants)
      ? e.participants.map((p) =>
          typeof p === "string" ? p : p.name || p.full_name || ""
        )
      : [],
  }));

  const getScoreColor = (score, max = 100) => {
    const percentage = (score / max) * 100;
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="mb-12">
      <h3 className="text-2xl font-semibold text-foreground mb-8">
        Board Performance
      </h3>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
        {/* First Column */}
        <div className="space-y-6">
          {/* Attendance Rate */}
          <OrganicShape className="p-4 text-center">
            <Calendar className="w-6 h-6 text-primary mx-auto mb-2" />
            <h4 className="text-xs font-medium text-foreground/80 mb-1">
              Attendance Rate
            </h4>
            <p
              className={`text-2xl font-bold ${getScoreColor(
                performanceData.attendanceRate
              )}`}
            >
              {performanceData.attendanceRate}%
            </p>
          </OrganicShape>

          {/* Churn Rate */}
          <OrganicShape className="p-4 text-center">
            <Users className="w-6 h-6 text-primary mx-auto mb-2" />
            <h4 className="text-xs font-medium text-foreground/80 mb-1">
              Churn Rate
            </h4>
            <p
              className={`text-2xl font-bold ${performanceData.churnRate != null ? getScoreColor(100 - performanceData.churnRate) : "text-muted-foreground"}`}
            >
              {performanceData.churnRate != null ? `${performanceData.churnRate}%` : "—"}
            </p>
          </OrganicShape>
        </div>

        {/* Second Column */}
        <div className="space-y-6">
          {/* Average Age */}
          <OrganicShape className="p-4 text-center">
            <User className="w-6 h-6 text-primary mx-auto mb-2" />
            <h4 className="text-xs font-medium text-foreground/80 mb-1">
              Average Age
            </h4>
            <p
              className={`text-2xl font-bold ${performanceData.averageAge != null ? getScoreColor(performanceData.averageAge, 65) : "text-muted-foreground"}`}
            >
              {performanceData.averageAge ?? "—"}
            </p>
          </OrganicShape>

          {/* Age Range */}
          <OrganicShape className="p-4 text-center">
            <Calendar className="w-6 h-6 text-primary mx-auto mb-2" />
            <h4 className="text-xs font-medium text-foreground/80 mb-1">
              Age Range
            </h4>
            <div className="flex justify-center gap-1 text-xs">
              <span className="font-semibold">
                {performanceData.youngestAge ?? "—"}
              </span>
              <span>-</span>
              <span className="font-semibold">
                {performanceData.oldestAge ?? "—"}
              </span>
            </div>
          </OrganicShape>
        </div>

        {/* Third Column */}
        <div className="space-y-6">
          {/* Education Average */}
          <OrganicShape className="p-4 text-center">
            <GraduationCap className="w-6 h-6 text-primary mx-auto mb-2" />
            <h4 className="text-xs font-medium text-foreground/80 mb-1">
              Avg Education
            </h4>
            <p
              className={`text-2xl font-bold ${performanceData.educationLevel != null ? getScoreColor(performanceData.educationLevel, 5) : "text-muted-foreground"}`}
            >
              {metrics.average_education_label || metrics.education_label || (performanceData.educationLevel != null ? `${performanceData.educationLevel}/5` : "—")}
            </p>
          </OrganicShape>

          {/* Education Range */}
          <OrganicShape className="p-4 text-center">
            <GraduationCap className="w-6 h-6 text-primary mx-auto mb-2" />
            <h4 className="text-xs font-medium text-foreground/80 mb-1">
              Education Range
            </h4>
            <div className="text-xs space-y-1">
              <div>
                Low:{" "}
                {metrics.lowest_education_label ||
                  metrics.education_range_low ||
                  (performanceData.lowestEducation != null
                    ? `Level ${performanceData.lowestEducation}`
                    : "—")}
              </div>
              <div>
                High:{" "}
                {metrics.highest_education_label ||
                  metrics.education_range_high ||
                  (performanceData.highestEducation != null
                    ? `Level ${performanceData.highestEducation}`
                    : "—")}
              </div>
            </div>
          </OrganicShape>
        </div>

        {/* Fourth Column - Average Board Expertises - Wider */}
        <OrganicShape className="p-6 lg:col-span-1.5">
          <User className="w-6 h-6 text-primary mx-auto mb-2" />
          <h4 className="text-sm font-medium text-foreground mb-4 text-center">
            Average Board Expertises
          </h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div className="flex justify-between">
              <span>Funding</span>
              <span className="font-semibold">3</span>
            </div>
            <div className="flex justify-between">
              <span>Lobbyism</span>
              <span className="font-semibold">2</span>
            </div>
            <div className="flex justify-between">
              <span>Industry knowledge</span>
              <span className="font-semibold">4</span>
            </div>
            <div className="flex justify-between">
              <span>Operations</span>
              <span className="font-semibold">5</span>
            </div>
            <div className="flex justify-between">
              <span>Sales</span>
              <span className="font-semibold">4</span>
            </div>
            <div className="flex justify-between">
              <span>Technology</span>
              <span className="font-semibold">6</span>
            </div>
            <div className="flex justify-between">
              <span>Finance</span>
              <span className="font-semibold">5</span>
            </div>
            <div className="flex justify-between">
              <span>Marketing</span>
              <span className="font-semibold">4</span>
            </div>
            <div className="flex justify-between">
              <span>Legal</span>
              <span className="font-semibold">3</span>
            </div>
            <div className="flex justify-between">
              <span>Human Resources</span>
              <span className="font-semibold">4</span>
            </div>
            <div className="flex justify-between">
              <span>Strategy</span>
              <span className="font-semibold">5</span>
            </div>
            <div className="flex justify-between">
              <span>Risk Management</span>
              <span className="font-semibold">4</span>
            </div>
            <div className="flex justify-between">
              <span>International</span>
              <span className="font-semibold">3</span>
            </div>
          </div>
        </OrganicShape>

        {/* Board Education Section - Wider */}
        <OrganicShape className="p-6 lg:col-span-1.5">
          <GraduationCap className="w-6 h-6 text-primary mx-auto mb-2" />
          <h4 className="text-sm font-medium text-foreground mb-4 text-center">
            Board Education
          </h4>
          <div className="space-y-3">
            {boardEducations.length > 0 ? (
              boardEducations.map((education) => (
                <div key={education.id} className="text-xs">
                  <p className="font-medium text-foreground/90 mb-1">
                    {education.title}
                  </p>
                  <p className="text-foreground/60 mb-1">{education.date}</p>
                  <p className="text-foreground/70">
                    {education.participants.length} participants
                  </p>
                </div>
              ))
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2">
                No education sessions recorded yet.
              </p>
            )}
          </div>
        </OrganicShape>
      </div>
    </div>
  );
};

export default BoardPerformance;
